import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AccessService } from 'app/access.service';
import { MenuMappingService } from 'app/menu-mapping.service';
import { UiNavSection } from 'app/shared/ui/models/ui-sidebar.model';

declare interface RouteInfo {
  path: string;
  title: string;
  icon: string;
  class: string;
  access?: boolean;
}

export const ROUTES: RouteInfo[] = [
  { path: 'vendor-master', title: 'Vendor Master', icon: 'person', class: '' },
  { path: 'vendor-branch-master', title: 'Vendor Branch Master', icon: 'person', class: '' },
  { path: 'voucher-posting-supplysync', title: 'Posted PI / SI', icon: 'receipt_long', class: '' },
  { path: 'voucher-posting-create-supplysync', title: 'Create PI / SI', icon: 'receipt_long', class: '' },
  { path: 'customer-master', title: 'Customer Master', icon: 'person', class: '' },
  { path: 'customer-branch-master', title: 'Customer Branch Master', icon: 'person', class: '' },
  { path: 'contact-person-master', title: 'Contact Person Master', icon: 'person', class: '' },
  { path: 'requisition-master', title: 'Purchase Requisition', icon: 'assignment', class: '' },
  { path: 'request-for-quotation-master', title: 'RFQ', icon: 'request_quote', class: '' },
  { path: 'quotation-receive', title: 'Quotation Receive', icon: 'library_books', class: '' },
  { path: 'quotation-comparision', title: 'Quotation Comparison', icon: 'compare_arrows', class: '' },
  { path: 'purchase-order', title: 'Purchase Order', icon: 'shopping_cart_checkout', class: '' },
  { path: 'grn-master', title: 'GSRN', icon: 'local_shipping', class: '' },
  { path: 'quality-check', title: 'Quality Check', icon: 'verified', class: '' },
  { path: 'sales-order', title: 'Sales Order', icon: 'shopping_cart_checkout', class: '' },
  { path: 'gsn-master', title: 'GSSN', icon: 'local_shipping', class: '' },
  { path: 'invoice-details', title: 'Invoice Details', icon: 'receipt_long', class: '' },
  { path: 'purchase-order-view', title: 'Purchase Order View', icon: 'visibility', class: '' },
  { path: 'requisition-report', title: 'Requisition Report', icon: 'assessment', class: '' },
  { path: 'purchase-order-manual', title: 'Purchase Order (Manual)', icon: 'edit_document', class: '' },
  { path: 'item-wise-report', title: 'Item Wise Report', icon: 'library_books', class: '' },
  { path: 'sales-return', title: 'Sales Return', icon: 'library_books', class: ''},
  { path: 'purchase-return', title: 'Purchase Return', icon: 'library_books', class: '' },
];

const SECTION_DEFINITIONS: { title: string; paths: string[] }[] = [
  {
    title: 'Masters',
    paths: [
      'vendor-master',
      'vendor-branch-master',
      'customer-master',
      'customer-branch-master',
      'contact-person-master',
    ],
  },
  {
    title: 'Procurement',
    paths: [
      'requisition-master',
      'request-for-quotation-master',
      'quotation-receive',
      'quotation-comparision',
      'purchase-order',
      'grn-master',
      'quality-check',
      'purchase-order-view',
      'requisition-report',
      'purchase-order-manual',
      'item-wise-report',
      'purchase-return',
    ],
  },
  {
    title: 'Sales & Invoicing',
    paths: [
      'voucher-posting-supplysync',
      'voucher-posting-create-supplysync',
      'sales-order',
      'gsn-master',
      'invoice-details',
      'sales-return',
    ],
  },
];

@Component({
  selector: 'app-supplysync-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
  menuItems: RouteInfo[] = [];
  menuSections: UiNavSection[] = [];
  activePath = '';
  companyName = 'Pradhan Associates';
  private userMenus: any[] = [];
  private user_id: any;
  private routerSub?: Subscription;

  @Input() isCollapsed = false;

  constructor(
    private accessService: AccessService,
    private menuMappingService: MenuMappingService,
    private router: Router
  ) {
    const userString = localStorage.getItem('data');
    const user = JSON.parse(userString) || {};
    this.user_id = user?.id;
    this.companyName = localStorage.getItem('companyname') || 'Pradhan Associates';
  }

  ngOnInit(): void {
    this.loadUserMenusAndApplyAccess();
    this.updateActivePath();
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.updateActivePath());
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  onNavigate(path: string): void {
    this.router.navigate(['/supplysync', path]);
  }

  private updateActivePath(): void {
    const segments = this.router.url.split('/').filter(Boolean);
    this.activePath = segments[segments.length - 1] || '';
  }

  private loadUserMenusAndApplyAccess(): void {
    if (this.user_id === 1) {
      this.menuItems = ROUTES.map(route => ({ ...route, access: true }));
      this.buildMenuSections();
      return;
    }

    this.accessService.getUserMenusFromConfig().subscribe(
      (menus) => {
        this.userMenus = menus;
        this.applyMenuAccess();
      },
      () => {
        this.menuItems = ROUTES.map(route => ({ ...route, access: true }));
        this.buildMenuSections();
      }
    );
  }

  private applyMenuAccess(): void {
    this.menuItems = ROUTES.map(route => ({
      ...route,
      access: this.menuMappingService.hasMenuAccess(route.path, this.userMenus),
    }));
    this.buildMenuSections();
  }

  private buildMenuSections(): void {
    const accessibleItems = this.menuItems.filter(item => item.access !== false);
    const itemByPath = new Map(accessibleItems.map(item => [item.path, item]));

    this.menuSections = SECTION_DEFINITIONS
      .map(section => ({
        title: section.title,
        items: section.paths
          .map(path => itemByPath.get(path))
          .filter((item): item is RouteInfo => !!item)
          .map(item => ({
            path: item.path,
            label: item.title,
            icon: item.icon,
          })),
      }))
      .filter(section => section.items.length > 0);
  }
}
